import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole = "admin" | "user";

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [tier, setTier] = useState<"free" | "pro">("free");
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      // Fire-and-forget activity logging for login/logout (RLS scoped to self).
      if (event === "SIGNED_IN" && s?.user) {
        const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
        supabase.rpc("log_user_activity" as never, {
          _event: "login",
          _description: "Signed in",
          _metadata: { provider: s.user.app_metadata?.provider ?? "email" },
          _user_agent: ua,
        } as never).then(() => {}, () => {});
      } else if (event === "SIGNED_OUT") {
        supabase.rpc("log_user_activity" as never, {
          _event: "logout",
          _description: "Signed out",
        } as never).then(() => {}, () => {});
      } else if (event === "PASSWORD_RECOVERY" || event === "USER_UPDATED") {
        supabase.rpc("log_user_activity" as never, {
          _event: "password_changed",
          _description: "Account credentials updated",
        } as never).then(() => {}, () => {});
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) {
      setRoles([]);
      setTier("free");
      setRolesLoading(false);
      return;
    }
    setRolesLoading(true);
    (async () => {
      const [{ data: r }, { data: pro }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase.rpc("is_pro_user", { _uid: userId }),
      ]);
      setRoles(((r ?? []) as { role: AppRole }[]).map((x) => x.role));
      setTier(pro ? "pro" : "free");
      setRolesLoading(false);
    })();
  }, [userId]);

  return {
    session,
    user,
    loading: loading || rolesLoading,
    roles,
    tier,
    isAdmin: roles.includes("admin"),
    isPro: tier === "pro",
    signOut: () => supabase.auth.signOut(),
  };
}
